<script lang="ts">
import { BetHistoryEntity, BetHistoryEntitySelection } from '@/common/api/model';
import { OrderType, TransactionType } from '@/common/config';
import { formatDate } from '@/common/date';
import { round } from '@/common/utils';
import FaCheckSquare from '@/components/icons/FaCheckSquare.vue';
import FaRegSquare from '@/components/icons/FaRegSquare.vue';
import RadioEmpty from '@/components/icons/RadioEmpty.vue';
import RadioFilled from '@/components/icons/RadioFilled.vue';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import TransactionItemWarning from './TransactionItemWarning.vue';

const userModule = namespace('user');
const getTitle = (firstSelection: BetHistoryEntitySelection) => {
  const isOutright = firstSelection.eventId.includes('match');
  return isOutright ? `${firstSelection.home} vs ${firstSelection.away}` : firstSelection.tournamentName;
};

@Component({
  components: {
    FaCheckSquare,
    FaRegSquare,
    RadioFilled,
    RadioEmpty,
    TransactionItemWarning,
  },
  filters: {
    formatDate(value: any) {
      return formatDate(value);
    },
    getTitle(firstSelection: BetHistoryEntitySelection) {
      return getTitle(firstSelection);
    },
  },
})
export default class BetTransactionItem extends Vue {
  @Prop() readonly transaction!: BetHistoryEntity;
  @Prop(Boolean) readonly isSelected!: boolean;
  @Prop(Boolean) readonly isSingleTransactionSelectMode!: boolean;
  @Prop(Boolean) readonly isUserEventsSelection!: boolean;
  // TODO: move to emits
  @Prop() selectIssueTransactions!: (item: BetHistoryEntity) => void;
  @Prop() handleSelectUserEventsSelections!: (item: BetHistoryEntitySelection) => void;
  @Prop() userEventsSelections!: BetHistoryEntitySelection[];
  @Prop({ default: '' }) readonly transactionType!: TransactionType;
  @Prop() expandTrans!: (shortId: string) => void;
  @Prop() expandedTrans!: string[];
  @Prop() handleExpandUserSelections!: (id: string) => void;
  @Prop() expandedUserEventsSelections!: string[];

  orderTypeMap = OrderType;

  get winningStatusMap() {
    return {
      0: this.tConversation('page.issue.bet.running'),
      5: this.tConversation('page.issue.bet.partial_win'),
      20: this.tConversation('page.issue.bet.won'),
      30: this.tConversation('page.issue.bet.lost'),
      40: this.tConversation('page.issue.bet.void'),
      90: this.tConversation('page.issue.bet.pending'),
    };
  }

  get userSelectionEventsStatusMap() {
    return {
      0: this.tConversation('page.issue.bet.running'),
      1: this.tConversation('page.issue.bet.won'),
      2: this.tConversation('page.issue.bet.lost'),
      3: this.tConversation('page.issue.bet.cancelled'),
      4: this.tConversation('page.issue.bet.void'),
      5: this.tConversation('page.issue.bet.win_and_refund_half'),
      6: this.tConversation('page.issue.bet.lost_and_refund_half'),
    };
  }

  @userModule.Getter('currency') currency!: string;

  get calculateOdds() {
    if (this.transaction.orderType === 1 && this.transaction.selections.length === 1) {
      return round(+this.transaction.selections[0].odds, 2);
    } else if (this.transaction.orderType === 2) {
      const val = this.transaction.selections.reduce((acc: number, curr: BetHistoryEntitySelection) => {
        return acc * +curr.odds;
      }, 1);
      return round(val, 2);
    }
    return false;
  }

  get getTotalWinnings() {
    const { winningStatus, totalWinnings } = this.transaction;
    return winningStatus === 0 || winningStatus === 40 ? '--' : totalWinnings;
  }

  get getReturnClass() {
    const { winningStatus } = this.transaction;
    if (winningStatus === 5 || winningStatus === 20) {
      return 'winnings';
    } else if (winningStatus === 30) {
      return 'losses';
    } else {
      return null;
    }
  }

  get idForTitle() {
    if (this.isUserEventsSelection) {
      return this.transaction.selections[0].eventId;
    }
    return this.transaction.shortId;
  }

  get handleExpandedTrans() {
    if (this.isUserEventsSelection) {
      return this.expandedUserEventsSelections.includes(this.transaction.selections[0]?.id);
    }
    return this.expandedTrans.includes(this.transaction.shortId);
  }

  handleExpandTrans() {
    if (this.isUserEventsSelection) {
      return this.handleExpandUserSelections(this.transaction.selections[0]?.id);
    }
    return this.expandTrans(this.transaction.shortId);
  }

  get betDetails() {
    return {
      stakeInfo: !this.isUserEventsSelection
        ? `${this.tConversation('page.issue.bet.total_stake')} (${this.currency}) ${this.transaction.totalStake}`
        : null,
      marketDescription:
        this.isUserEventsSelection && this.transaction.selections?.[0]?.marketDesc
          ? `${this.tConversation('page.issue.bet.market')}: ${this.transaction.selections[0].marketDesc}`
          : null,
      statusText: !this.isUserEventsSelection
        ? this.winningStatusMap[this.transaction.winningStatus]
        : this.userSelectionEventsStatusMap[this.transaction.selections?.[0]?.status],
    };
  }

  handleSelectTransaction() {
    if (this.isUserEventsSelection) {
      this.handleSelectUserEventsSelections(this.transaction.selections[0]);
      return false;
    }
    return this.selectIssueTransactions(this.transaction);
  }
}
</script>

<template>
  <!-- Some case the selections is empty from QA -->
  <div>
    <div v-if="transaction.selections && transaction.selections[0]" class="transaction__header">
      <span v-if="isSelected" @click.prevent="handleSelectTransaction">
        <FaCheckSquare
          v-if="!isSingleTransactionSelectMode || isUserEventsSelection"
          height="2.1rem"
          width="2.1rem"
          class="checked transaction__checkbox"
        />
        <RadioFilled v-else height="2.1rem" width="2.1rem" class="checked transaction__checkbox" />
      </span>
      <span v-else @click.prevent="handleSelectTransaction">
        <FaRegSquare
          v-if="!isSingleTransactionSelectMode || isUserEventsSelection"
          height="2.1rem"
          width="2.1rem"
          class="transaction__checkbox unchecked"
        />
        <RadioEmpty v-else height="2.1rem" width="2.1rem" class="transaction__checkbox unchecked" />
      </span>
      <div class="separate" />
      <div class="title">
        <div class="title__description">
          <span>{{ transaction.selections[0] | getTitle }}</span>

          <span v-if="transaction.selections.length > 1"> and {{ transaction.selections.length - 1 }} more </span>
          <span>
            (Id: <code>{{ idForTitle }}</code
            >)</span
          >
        </div>
        <div class="title__bet-details">
          <span v-if="betDetails.stakeInfo">{{ betDetails.stakeInfo }}</span>
          <span v-if="betDetails.marketDescription">{{ betDetails.marketDescription }}</span>
          <span>{{ betDetails.statusText }}</span>
        </div>
      </div>
      <div
        v-if="!isUserEventsSelection"
        :class="{
          arrow: !isUserEventsSelection,
          arrowUserSelections: isUserEventsSelection,
        }"
        @click.prevent="handleExpandTrans"
      />
    </div>
    <div v-if="handleExpandedTrans" class="transaction__details">
      <div class="transaction__details--amount">
        <span>{{ tConversation('page.issue.bet.total_stake') }} ({{ currency }})</span>
        <span>{{ transaction.totalStake }}</span>
      </div>
      <div class="transaction__details--status">
        <span>{{ tConversation('page.issue.bet.total_return') }} ({{ currency }})</span>
        <span class="return" :class="getReturnClass">
          {{ getTotalWinnings }}
        </span>
      </div>
      <div v-if="!!calculateOdds">
        <span>{{ tConversation('page.issue.bet.odds') }}</span>
        <span class="data">{{ calculateOdds }}</span>
      </div>
      <div>
        <span>{{ tConversation('page.issue.status') }}</span>
        <span class="data">{{ winningStatusMap[transaction.winningStatus] }}</span>
      </div>
      <div>
        <span>{{ tConversation('page.issue.bet.type') }}</span>
        <span class="data">{{ orderTypeMap[transaction.orderType] }}</span>
      </div>
      <div>
        <span>{{ tConversation('global.time') }}</span>
        <span class="data">{{ transaction.createTime | formatDate }}</span>
      </div>
      <div>
        <span>{{ tConversation('page.issue.total_matches') }}</span>
        <span class="data">{{ transaction.selections.length }}</span>
      </div>
    </div>
    <TransactionItemWarning
      v-if="!isUserEventsSelection && isSelected"
      :transaction="transaction"
      :transaction-type="transactionType"
    />
  </div>
</template>
